/**
 * The vocabulary a landing report is written in, shared by the store and the
 * portal.
 *
 * Both apps lose players in the same window — after we answer the request and
 * before our bundle runs — and a publisher asking "what happened to this
 * player" does not care which of the two they were handed to. Answering across
 * both means the two sides have to agree on the words, so the stages, the join
 * key and the payload check live here rather than once per app.
 *
 * Everything here is pure and runtime-agnostic on purpose: the same definitions
 * are read by edge middleware, by a node route handler and by a test, and the
 * static reporter served to the browser is held to them by a test in each app.
 */

/** Where both apps receive their reports. Outside either middleware matcher, so it logs no landings of its own. */
export const LANDING_BEACON_ROUTE = '/api/landing-beacon';

/**
 * The reporter itself, served as an ordinary static file from each app's public
 * directory.
 *
 * A file rather than an inlined string on purpose: text assembled into a script
 * is a place where a value can become code, and this one is configured from a
 * request. As a file it is fixed at build time and the landing it describes
 * reaches it as data attributes, which a browser cannot execute.
 */
export const LANDING_BEACON_SRC = '/landing-beacon.js';

/** How the script finds its own tag, and so its configuration. */
export const LANDING_BEACON_ELEMENT_ID = 'ac-landing-beacon';

/**
 * Whether the document has already sent its first report.
 *
 * Set by the inline snippet and read by the served file, so a landing that runs
 * both counts once rather than twice.
 */
export const LANDING_BEACON_PARSED_FLAG = '__acLandingParsed';

/**
 * The first report, inlined into the document.
 *
 * The reporter is an async file, so every report depends on a second request
 * arriving — and a landing whose connection dies before it does leaves no
 * evidence at all. That is not a small gap: over a week the portal answered
 * 35,588 credential landings and 13,182 reported anything, so the observer was
 * missing for nearly two thirds of exactly the population it exists to explain,
 * because it needed the same network the page needed. This is the fix: the one
 * report that cannot afford a request of its own is sent from the document.
 *
 * Kept to the first report only. Inlining the whole reporter would put seven
 * kilobytes in every landing's HTML to describe a wait, and the later stages are
 * only reachable by a document that survived long enough to have received the
 * file anyway.
 *
 * A fixed string, assembled from nothing and interpolated into never — the
 * property that makes the reporter a served file rather than built text holds
 * here for the same reason, and a test enforces it. The landing reaches it the
 * same way too: read off the tag's own attributes, which a browser cannot
 * execute. The literals are deliberate rather than built from the constants
 * around them, and tests hold the two in step.
 */
export const LANDING_BEACON_FIRST_REPORT =
    '(function(){try{' +
    // Nothing to report without a landing, and nowhere to report it without the
    // one transport that survives a page being torn down.
    "var t=document.getElementById('ac-landing-beacon');" +
    'if(!t||!navigator.sendBeacon)return;' +
    "var k=t.getAttribute('data-key'),c=t.getAttribute('data-credential'),j=t.getAttribute('data-journey');" +
    'if(!k||!c)return;' +
    // Tells the file, if it ever arrives, that this one is already sent.
    'window.__acLandingParsed=1;' +
    'var n=navigator.connection||navigator.mozConnection||navigator.webkitConnection;' +
    "navigator.sendBeacon('/api/landing-beacon',new Blob([JSON.stringify({" +
    "stage:'parsed',key:k,credential:c,journeyId:j," +
    "elapsedMs:typeof performance!=='undefined'&&performance.now?Math.round(performance.now()):0," +
    'readyState:document.readyState,' +
    // A warm cache can run the app before the document finishes parsing.
    "booted:!!window[t.getAttribute('data-flag')||'__acPortalBooted']," +
    'interacted:false,' +
    'network:n&&n.effectiveType?n.effectiveType:undefined,' +
    // Whether a player is being shown this at all, which is the whole reason a
    // report that arrives on its own is worth having.
    'visibility:document.visibilityState,' +
    "everVisible:document.visibilityState==='visible'," +
    'prerendering:document.prerendering===true,' +
    // Whether this is a re-open after the browser killed the previous tab: the
    // one fact about a lost page that the *next* page can report.
    'wasDiscarded:document.wasDiscarded===true' +
    "})],{type:'text/plain'}))" +
    '}catch(e){}})();';

/**
 * How the app tells the reporter it is running.
 *
 * A global alone cannot say *when* it was set, and the reporter would have to
 * poll to find out — so the app announces it and the reporter listens. It still
 * reads the global as well, because the two race: the reporter is fetched async
 * and on a warm cache the app can boot first, in which case the announcement is
 * already over by the time there is anything listening for it.
 */
export const LANDING_BEACON_BOOTED_EVENT = 'ac-landing-booted';

/**
 * How the app tells the reporter the player can see something.
 *
 * Booting is not seeing. The portal mounts, announces the boot, and then may
 * hold every pixel back while it authenticates — so a landing that leaves during
 * that hold has `booted: true` and looked at a blank page the whole time. This
 * marks the commit that actually put the interface on screen, and it is what
 * turns "booted and left" into "saw the portal and left" or "never saw it".
 */
export const LANDING_BEACON_RENDERED_EVENT = 'ac-landing-rendered';

/**
 * Which global the reporter reads to see whether the app is running, named on
 * its own tag because the two apps set different ones.
 */
export const LANDING_BEACON_FLAG_ATTRIBUTE = 'data-flag';

/**
 * The states the app may leave in its authentication global, read by the
 * reporter at every report. The one thing that tells a page waiting on the
 * network from a page whose login threw and will never finish: the first is
 * `pending` and later `ok`, the second is `pending` at the moment it is left.
 */
export const LANDING_BEACON_AUTH_STATES: string[] = ['pending', 'ok', 'failed'];

/**
 * Whether this document wants the errors on it reported.
 *
 * The one signal a served file cannot decide for itself: the same reporter is
 * served by both apps, but only the one under investigation should pay for
 * listening. So the document that wants it says so on the tag, and the reporter
 * attaches its error listeners only when it is asked — keeping the file byte
 * identical across apps while the behaviour stays with whoever opted in.
 */
export const LANDING_BEACON_ERRORS_ATTRIBUTE = 'data-errors';

/**
 * How the request-time view of a landing reaches the render.
 *
 * A layout is never handed searchParams, so without these the server render
 * cannot tell it is serving a credential landing at all — and the credential
 * itself must not be the thing forwarded. Each middleware strips both off the
 * incoming request before setting them, so a caller cannot supply its own.
 */
export const LANDING_KEY_HEADER = 'x-ac-landing-key';
export const LANDING_CREDENTIAL_HEADER = 'x-ac-landing-credential';
export const LANDING_JOURNEY_HEADER = 'x-ac-landing-journey';

const LANDING_JOURNEY_ID_PATTERN = /^[a-zA-Z0-9-]{1,128}$/;

export const isLandingJourneyId = (value: unknown): value is string =>
    typeof value === 'string' && LANDING_JOURNEY_ID_PATTERN.test(value);

export enum ELandingBeaconStage {
    /**
     * A real browser parsed our document as far as the reporter's own tag. Proves
     * the response arrived and rendered, which nothing else on the client can:
     * paired with the server's landing log, its absence means we answered and the
     * browser never got far enough to run three kilobytes.
     */
    PARSED = 'parsed',
    /**
     * Still running, at a known point in the wait.
     *
     * The signal that makes silence readable. A landing that reports `parsed` and
     * nothing else is the largest unexplained group we have, and it has two
     * completely different causes — the process was killed, or the page was still
     * loading when the network went. Neither can announce itself, because both
     * take the reporter with them. Bracketing the wait turns "silent" into "was
     * alive at four seconds, gone by eight", which is the difference between a
     * player who left and a page we lost.
     */
    ALIVE = 'alive',
    /**
     * Our code started. The one stage the app itself announces, so boot latency
     * is known for every credential landing rather than for the sampled few that
     * survive long enough to send client telemetry.
     */
    BOOTED = 'booted',
    /**
     * The interface reached the screen. Announced by the app at the commit that
     * ends the blank page, so time-to-something-visible is known for every
     * landing, and a landing that booted but never reached this was looking at
     * nothing for as long as it stayed.
     */
    RENDERED = 'rendered',
    /** Backgrounded: the tab is no longer in front of the player. */
    HIDDEN = 'hidden',
    /**
     * Back in front of the player after being backgrounded.
     *
     * Distinguishes leaving from looking away, which nothing else does. A landing
     * that hides and never returns was abandoned; one that hides and comes back
     * was interrupted, and if it still fails afterwards the cause is ours.
     */
    VISIBLE = 'visible',
    /**
     * The browser froze the tab, so it holds no CPU while backgrounded.
     *
     * Proof of a specific fate rather than an inference. A frozen page is intact
     * and stopped; a killed one is gone. Both look identical from the server, and
     * only the browser can tell us which happened — this is it telling us.
     */
    FROZEN = 'frozen',
    /** Unfrozen and running again, so anything after this is a resumed page rather than a fresh one. */
    RESUMED = 'resumed',
    /** Going away for good, and how far the load had got by then. */
    LEFT = 'left',
    /**
     * Something threw where the player could see it. The stage that turns `left`
     * into "saw an error, then left": a landing that reports an error and then
     * leaves left because of us, and one that leaves without one left for a
     * reason that was never on the page. Carried with the error's name and a
     * short message, and nothing the page itself held.
     *
     * Reported by the app being investigated for errors and no other, so the
     * same reporter is served everywhere while only that app listens — see
     * LANDING_BEACON_ERRORS_ATTRIBUTE.
     */
    ERROR = 'error',
    RECOVERY = 'recovery',
}

export const LANDING_BEACON_STAGES: string[] = Object.values(ELandingBeaconStage);
export const LANDING_BEACON_RECOVERY_SOURCES: string[] = [
    'chunk',
    'route-error',
    'global-error',
];
export const LANDING_BEACON_RECOVERY_PHASES: string[] = [
    'reload-dispatched',
    'fallback-displayed',
    'retry-selected',
    'portal-selected',
];

/**
 * When to confirm the page is still running, while it still has nothing to show.
 *
 * Spaced to match where landings are actually lost — a quarter are gone inside
 * two seconds and most of the rest by fifteen — so an unexplained landing is
 * placed in a bracket rather than merely counted. Reporting stops at boot,
 * because after that the app speaks for itself and every further report would be
 * volume without an answer.
 */
export const LANDING_BEACON_MILESTONES_MS: readonly number[] = [1_000, 2_500, 5_000, 10_000, 20_000];

/**
 * The connection classes a report may claim.
 *
 * Recorded because the leading explanation for these losses is that the player
 * was on a connection too slow to finish the download, and that has only ever
 * been inferred from the timings of the players who survived to be sampled. Read
 * from the browser's own estimate at report time, which is available on exactly
 * the mobile Chromium engines most of this traffic runs on.
 */
export const LANDING_BEACON_NETWORKS: string[] = ['slow-2g', '2g', '3g', '4g'];

/**
 * The visibility states a report may claim.
 *
 * `prerender` is the legacy value some engines still report for a speculatively
 * loaded document; the modern signal is `document.prerendering`, carried
 * separately because the two do not agree across engines.
 */
export const LANDING_BEACON_VISIBILITIES: string[] = ['visible', 'hidden', 'prerender'];

/** A browsing context being navigated, as opposed to a fetch or XHR (`empty`). */
const NAVIGATED_DESTINATIONS = new Set(['document', 'iframe', 'frame']);

/**
 * Whether a request is a player arriving, rather than the framework fetching the
 * same URL again.
 *
 * Next refetches a URL for RSC payloads and prefetches, and counting those would
 * turn one arrival into several. It cannot be detected by anything Next sets,
 * though: the adapter deletes FLIGHT_HEADERS (including `RSC`) from the request
 * and strips `_rsc` off the URL before middleware runs, so a payload refetch
 * arrives looking bare, and checking for those markers silently matches nothing.
 *
 * So the test is inverted onto headers the browser sets and Next leaves alone:
 * count only what is actually a document navigation. Shared by both apps because
 * two funnels that disagree about what an arrival is cannot be compared.
 */
export const isDocumentNavigation = (headers: Headers): boolean => {
    if (isSpeculativePrefetch(headers)) {
        return false;
    }

    const destination = headers.get('sec-fetch-dest');
    if (destination) {
        return NAVIGATED_DESTINATIONS.has(destination);
    }

    // Browsers too old for Sec-Fetch-Dest still ask for HTML when navigating,
    // which an RSC fetch never does.
    return headers.get('accept')?.includes('text/html') ?? false;
};

// A link the browser fetched ahead of time is not a player arriving.
const isSpeculativePrefetch = (headers: Headers): boolean =>
    [headers.get('sec-purpose'), headers.get('purpose')].some((value) => value?.includes('prefetch'));

/** 64 bits of a sha256: reproducible by the publisher, collision-free at this volume. */
const LANDING_KEY_LENGTH = 16;

/** A truncated sha256 and nothing else; anything else is not ours. */
export const isLandingKey = (value: unknown): value is string =>
    typeof value === 'string' && new RegExp(`^[0-9a-f]{${LANDING_KEY_LENGTH}}$`).test(value);

/**
 * The join key for a landing: a hash of the credential the publisher minted.
 *
 * The publisher mints the proofKey, so a hash of it is something they can
 * reproduce on their side to find the player behind a landing — without a live
 * credential being written to a log. Deliberately never the token: that one is
 * the credential the gateway exchanges.
 */
export const hashLandingKey = async (value: string | null | undefined): Promise<string | undefined> => {
    if (!value) {
        return undefined;
    }

    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));

    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, LANDING_KEY_LENGTH);
};

/**
 * Tells the in-document reporter that the app is running.
 *
 * Sets the global and announces the change, because the reporter may be waiting
 * on either: it reads the global when it starts, in case the app got there
 * first, and listens for the announcement in case it did not. Callers are inside
 * a mount effect on the critical path, so this cannot be allowed to throw.
 */
export const announceLandingBoot = (flag: string): void =>
    announceLandingMilestone(flag, LANDING_BEACON_BOOTED_EVENT);

/** Same again for the commit that put the interface on screen. */
export const announceLandingRender = (flag: string): void =>
    announceLandingMilestone(flag, LANDING_BEACON_RENDERED_EVENT);

/**
 * The global holds *when* rather than merely *that*: the milliseconds since
 * navigation, never below one so it still reads as set. A reporter that arrives
 * after the announcement is over can then still say when the milestone was
 * reached, instead of only that it was.
 */
const announceLandingMilestone = (flag: string, event: string): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        (window as unknown as Record<string, number>)[flag] = Math.max(1, Math.round(performance.now()));
        window.dispatchEvent(new Event(event));
    } catch {
        // Being unable to describe the boot must never be able to prevent it.
    }
};

/**
 * Where the login stands, for the reporter to carry on every report.
 *
 * Set by the app at each turn of its own authentication, read by the reporter
 * without either knowing the other's timing. Anything outside the known states
 * clears it, so a report can only ever carry a word the receiver accepts.
 */
export const markLandingAuth = (flag: string, state: string): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        (window as unknown as Record<string, string | undefined>)[flag] =
            LANDING_BEACON_AUTH_STATES.includes(state) ? state : undefined;
    } catch {
        // Same again: describing the login must never be able to break it.
    }
};

export interface LandingBeaconReport {
    stage: string;
    key: string;
    credential: string;
    journeyId?: string;
    elapsedMs: number;
    readyState: string;
    booted: boolean;
    /**
     * Whether the interface had reached the screen, and when each of the two
     * milestones was reached, in milliseconds since navigation.
     *
     * Together with `elapsedMs` these make a single exit report a timeline:
     * parsed at one time, booted at another, on screen at a third, left at a
     * fourth. A landing that left with `booted` and not `rendered` was looking
     * at a blank page for the whole of the difference, and that is the
     * question the boot flag alone could never answer.
     */
    rendered: boolean;
    bootedAtMs?: number;
    renderedAtMs?: number;
    /** Where the login stood at report time, on a landing that runs one. */
    auth?: string;
    /**
     * When the browser first painted text or an image, from its own Paint Timing
     * record — a background colour does not count. Absent on an exit report, it
     * is the browser itself saying nothing was ever shown but the background:
     * the one signal that settles "they saw a blank page" without inference.
     */
    fcpMs?: number;
    /**
     * How long the document was actually in front of the player, summed over
     * every visible interval. `elapsedMs` counts a backgrounded page too; this
     * is the time somebody spent looking at whatever was on screen.
     */
    visibleMs: number;
    /**
     * On a `left` report, whether the browser kept the document for its
     * back-forward cache. `false` is the document being destroyed — the tab
     * closed, the Custom Tab dismissed, or a navigation away — rather than a
     * page the player may come back to.
     */
    persisted?: boolean;
    /** The browser saying this load is a re-open after it discarded the previous tab. */
    wasDiscarded: boolean;
    interacted: boolean;
    network?: string;
    /**
     * Whether the document was on screen when it reported, and whether it had
     * ever been on screen at all.
     *
     * The pair that separates a player we lost from a page no player ever saw.
     * The largest unexplained group we have reports `parsed` and then nothing,
     * and that has two opposite explanations: a real player whose page the
     * platform destroyed, or a URL the browser loaded speculatively into a tab
     * it never showed and later discarded. Both are silent, both are
     * indistinguishable from the server, and they call for opposite responses —
     * the first is a page to make cheaper, the second is not a lost player at
     * all and should be excluded from the loss rate rather than fixed.
     *
     * `everVisible` is what actually decides it, because a speculative load is
     * hidden from its first moment and can never report a later stage: its
     * timers are throttled and its teardown is silent. A single `parsed` report
     * that was never visible is a phantom; one that was visible is a real
     * player, and the wait we measured is a wait somebody sat through.
     */
    visibility?: string;
    everVisible: boolean;
    /** The browser saying outright that this document is a speculative pre-render. */
    prerendering: boolean;
    /** Present only on an `error` stage: the error's name and a short, bounded message. */
    errorName?: string;
    errorMessage?: string;
    recoverySource?: string;
    recoveryPhase?: string;
}

/** A beacon is fire and forget, so the body is never large and never trusted. */
const MAX_BODY_BYTES = 1200;
const MAX_ELAPSED_MS = 30 * 60 * 1000;
const READY_STATES: string[] = ['loading', 'interactive', 'complete'];

/**
 * The error name and message are the one part of a report whose text the browser
 * did not choose from a fixed set, so they are the least trusted thing here:
 * both are bounded, and the message — which the log writes on one line — is
 * stripped of the control characters a message could otherwise use to forge log
 * structure.
 */
const MAX_ERROR_NAME_LENGTH = 40;
const MAX_ERROR_MESSAGE_LENGTH = 120;

const stripControl = (value: string): string => {
    let out = '';
    for (let i = 0; i < value.length; i += 1) {
        const code = value.charCodeAt(i);
        out += code < 0x20 || code === 0x7f ? ' ' : value[i];
    }

    return out;
};

const readErrorText = (value: unknown, max: number): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const cleaned = stripControl(value).trim().slice(0, max).trim();

    return cleaned.length > 0 ? cleaned : undefined;
};

const parseLandingBeaconBody = (body: string): Record<string, unknown> | undefined => {
    if (!body || body.length > MAX_BODY_BYTES) {
        return undefined;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(body);
    } catch {
        return undefined;
    }

    if (!parsed || typeof parsed !== 'object') {
        return undefined;
    }

    return parsed as Record<string, unknown>;
};

export interface LandingRecoveryReport {
    stage: ELandingBeaconStage.RECOVERY;
    recoverySource: string;
    recoveryPhase: string;
    /**
     * What the boundary caught, bounded the same way as an `error` report.
     * A recovery screen shown without these is a crash counted and not
     * identified, which is what left a week of them unexplained.
     */
    errorName?: string;
    errorMessage?: string;
}

export const readLandingRecoveryReport = (
    body: string
): LandingRecoveryReport | undefined => {
    const candidate = parseLandingBeaconBody(body);
    if (
        candidate?.stage !== ELandingBeaconStage.RECOVERY ||
        !isOneOf(candidate.recoverySource, LANDING_BEACON_RECOVERY_SOURCES) ||
        !isOneOf(candidate.recoveryPhase, LANDING_BEACON_RECOVERY_PHASES)
    ) {
        return undefined;
    }

    return {
        stage: ELandingBeaconStage.RECOVERY,
        recoverySource: candidate.recoverySource,
        recoveryPhase: candidate.recoveryPhase,
        errorName: readErrorText(candidate.errorName, MAX_ERROR_NAME_LENGTH),
        errorMessage: readErrorText(candidate.errorMessage, MAX_ERROR_MESSAGE_LENGTH),
    };
};

/**
 * Reads a beacon body into a report, or into nothing.
 *
 * The endpoint this serves is public and unauthenticated by necessity — it
 * reports on players who have not logged in, and by definition never will if
 * something goes wrong — so nothing here is believed. Every field is checked
 * against a known set and anything unrecognised drops the whole report rather
 * than being logged, because the one thing this must not become is a way to
 * write arbitrary text into our logs.
 */
export const readLandingBeaconReport = (
    body: string,
    allowedCredentials: string[]
): LandingBeaconReport | undefined => {
    const candidate = parseLandingBeaconBody(body);
    if (!candidate) {
        return undefined;
    }
    const { stage, key, credential, readyState, network, visibility, auth } = candidate;

    if (
        !isLandingKey(key) ||
        !isOneOf(stage, LANDING_BEACON_STAGES) ||
        !isOneOf(credential, allowedCredentials) ||
        !isOneOf(readyState, READY_STATES)
    ) {
        return undefined;
    }

    const journeyId = isLandingJourneyId(candidate.journeyId)
        ? candidate.journeyId
        : undefined;
    const recoverySource = isOneOf(
        candidate.recoverySource,
        LANDING_BEACON_RECOVERY_SOURCES
    )
        ? candidate.recoverySource
        : undefined;
    const recoveryPhase = isOneOf(
        candidate.recoveryPhase,
        LANDING_BEACON_RECOVERY_PHASES
    )
        ? candidate.recoveryPhase
        : undefined;

    if (
        stage === ELandingBeaconStage.RECOVERY &&
        (recoverySource === undefined || recoveryPhase === undefined)
    ) {
        return undefined;
    }

    return {
        stage,
        key,
        credential,
        ...(journeyId ? { journeyId } : {}),
        readyState,
        elapsedMs: readElapsed(candidate.elapsedMs),
        booted: candidate.booted === true,
        rendered: candidate.rendered === true,
        // Absent until the milestone is reached, and absent on documents served
        // before the app recorded when — neither is a malformed report.
        bootedAtMs: readOptionalElapsed(candidate.bootedAtMs),
        renderedAtMs: readOptionalElapsed(candidate.renderedAtMs),
        auth: isOneOf(auth, LANDING_BEACON_AUTH_STATES) ? auth : undefined,
        fcpMs: readOptionalElapsed(candidate.fcpMs),
        visibleMs: readElapsed(candidate.visibleMs),
        persisted: typeof candidate.persisted === 'boolean' ? candidate.persisted : undefined,
        wasDiscarded: candidate.wasDiscarded === true,
        interacted: candidate.interacted === true,
        // Absent on engines that do not estimate it, which is a fact about the
        // browser rather than a malformed report, so it does not drop the rest.
        network: isOneOf(network, LANDING_BEACON_NETWORKS) ? network : undefined,
        // Same treatment as the network: an engine that names a state we do not
        // know still has a report worth keeping, and `everVisible` — the field
        // the phantom-load question actually turns on — is a plain boolean that
        // no engine can garble.
        visibility: isOneOf(visibility, LANDING_BEACON_VISIBILITIES) ? visibility : undefined,
        everVisible: candidate.everVisible === true,
        prerendering: candidate.prerendering === true,
        // Present only on an error report; bounded and de-fanged rather than
        // rejected, so a malformed message costs the message and not the report.
        errorName: readErrorText(candidate.errorName, MAX_ERROR_NAME_LENGTH),
        errorMessage: readErrorText(candidate.errorMessage, MAX_ERROR_MESSAGE_LENGTH),
        recoverySource,
        recoveryPhase,
    };
};

const isOneOf = (value: unknown, allowed: string[]): value is string =>
    typeof value === 'string' && allowed.includes(value);

// Clamped rather than rejected: a wrong clock should not cost us the report.
const readElapsed = (value: unknown): number => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return 0;
    }

    return Math.min(Math.round(value), MAX_ELAPSED_MS);
};

const readOptionalElapsed = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0 ? readElapsed(value) : undefined;
